import React from 'react';
import type { ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getModernFormField, layout } from '../../../theme/theme';
import SearchableDropdown, { DropdownOption } from './SearchableDropdown';
import LabelWithTooltip from '../common/LabelWithTooltip';
import type { HelpValue } from '../common/helpModel';

// Re-export DropdownOption for backward compatibility
export type { DropdownOption };

interface FormSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  help?: HelpValue;
  tooltip?: HelpValue;
  error?: string;
  minWidth?: number;
  validate?: (value: string) => string | null;
  disabled?: boolean;
  placeholder?: string;
  allowCustomValue?: boolean;
  helperText?: string;
  showInlineHint?: boolean;
  dense?: boolean;
  labelAccessory?: ReactNode;
  sx?: object;
  'aria-label'?: string;
  validationFocused?: boolean;
}

/**
 * Form select with label - wraps the centralized SearchableDropdown
 */
const FormSelect: React.FC<FormSelectProps> = ({
  label,
  value,
  onChange,
  options,
  help,
  tooltip,
  error,
  validate,
  disabled = false,
  placeholder,
  allowCustomValue = true,
  helperText,
  showInlineHint = false,
  dense = false,
  labelAccessory,
  sx = {},
  'aria-label': ariaLabelProp,
  validationFocused = false,
}) => {
  const theme = useTheme();
  const formFieldStyles = React.useMemo(() => getModernFormField(theme), [theme]);
  const isRequired = label.includes('*');
  const cleanLabel = label.replace('*', '').trim();
  const preservesLeadingCase =
    /^(?:BOM|ID|ISO|PCF|PipeEX|Revit)\b/.test(cleanLabel) ||
    /^[A-Z0-9][A-Z0-9-]*$/.test(cleanLabel) ||
    /[a-z][A-Z]/.test(cleanLabel);
  const actionObjectLabel = preservesLeadingCase
    ? cleanLabel
    : `${cleanLabel.charAt(0).toLocaleLowerCase()}${cleanLabel.slice(1)}`;

  const handleChange = (newValue: string) => {
    onChange(newValue);
    if (validate) {
      validate(newValue);
    }
  };

  return (
    <Box
      data-validation-focus={validationFocused ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mb: dense ? 0 : 2.25,
        width: '100%',
        // No overflow clipping here: the outlined fieldset's bottom border
        // lands exactly on this box's bottom edge, so `overflow: hidden`
        // erased it whenever sub-pixel alignment flipped (e.g. after the
        // setup-confirmation strip changed height and shifted the fields).
        px: validationFocused ? (dense ? 0.875 : 1) : 0,
        py: validationFocused ? (dense ? 0.625 : 0.75) : 0,
        mx: validationFocused ? -1 : 0,
        borderRadius: validationFocused ? 1.5 : 0,
        backgroundColor: validationFocused ? alpha(theme.palette.warning.main, 0.08) : 'transparent',
        boxShadow: validationFocused ? `inset 2px 0 0 ${theme.palette.warning.main}` : 'none',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: dense ? 0.5 : 0.75 }}>
          <LabelWithTooltip
            label={cleanLabel}
            required={isRequired}
            minWidth={layout.minButtonWidth}
            help={help}
            tooltip={tooltip}
            helperText={helperText}
            showInlineHint={showInlineHint}
            accessory={labelAccessory}
          />
        </Box>
        <SearchableDropdown
          value={value}
          onChange={handleChange}
          options={options}
          placeholder={placeholder || (allowCustomValue ? 'Type or search…' : 'Search…')}
          error={error}
          disabled={disabled}
          freeSolo={allowCustomValue}
          ariaLabel={ariaLabelProp || `Select ${actionObjectLabel}`}
          sx={{
            ...formFieldStyles,
            ...sx,
          }}
        />
      </Box>
    </Box>
  );
};

export default FormSelect;
